import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { auth, APIError } from '../api/client'
import { AuthLayout } from '../components/layout/AuthLayout'
import {
  Alert,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
} from '../components/ui'

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await auth.forgotPassword(email)
      setSubmitted(true)
    } catch (err) {
      if (err instanceof APIError) {
        setError(err.message)
      } else {
        setError('An unexpected error occurred')
      }
    } finally {
      setLoading(false)
    }
  }

  if (submitted) {
    return (
      <AuthLayout>
        <Card>
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-[var(--success-muted)]/30">
              <svg
                className="h-6 w-6 text-[var(--success)]"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <CardTitle>Check your email</CardTitle>
            <CardDescription>
              If an account exists for {email}, we've sent password reset instructions.
            </CardDescription>
          </CardHeader>

          <CardContent>
            <div className="text-center text-sm text-[var(--text-secondary)]">
              <p className="mb-4">
                Check your inbox and click the link to reset your password.
                The link will expire in 1 hour.
              </p>
              <p>
                Didn't receive an email?{' '}
                <button
                  type="button"
                  onClick={() => setSubmitted(false)}
                  className="font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
                >
                  Try again
                </button>
              </p>
            </div>
          </CardContent>

          <CardFooter>
            <Link
              to="/login"
              className="font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
            >
              &larr; Back to sign in
            </Link>
          </CardFooter>
        </Card>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <Card>
        <CardHeader>
          <CardTitle>Forgot password?</CardTitle>
          <CardDescription>
            Enter your email and we'll send you a link to reset your password
          </CardDescription>
        </CardHeader>

        <CardContent>
          {error && (
            <Alert variant="error" className="mb-4">
              {error}
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <Input
              label="Email"
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              autoComplete="email"
              autoFocus
            />

            <Button type="submit" fullWidth loading={loading}>
              Send reset link
            </Button>
          </form>
        </CardContent>

        <CardFooter>
          <Link
            to="/login"
            className="font-medium text-[var(--text-primary)] hover:opacity-80 transition-opacity"
          >
            &larr; Back to sign in
          </Link>
        </CardFooter>
      </Card>
    </AuthLayout>
  )
}
